import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserPlus, Search, Check, Trash2, Shield } from 'lucide-react';
import { Button } from '../ui/Button';
import { useTheme } from '../../contexts/ThemeContext';

export interface AccessMember {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: 'viewer' | 'editor' | 'admin';
}

interface RestrictedAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  streamName: string;
  members: AccessMember[];
  onAddMember: (email: string, role: AccessMember['role']) => void;
  onRemoveMember: (id: string) => void;
  onUpdateRole: (id: string, role: AccessMember['role']) => void;
}

// Mock available users for search (in real app, this would be an API call)
const AVAILABLE_USERS = [
  { id: 'u1', name: 'Sarah Chen', email: 'sarah@acme.com', avatar: 'SC' },
  { id: 'u2', name: 'Mike Ross', email: 'mike@acme.com', avatar: 'MR' },
  { id: 'u3', name: 'Alex Kim', email: 'alex@acme.com', avatar: 'AK' },
  { id: 'u4', name: 'Jordan Lee', email: 'jordan@acme.com', avatar: 'JL' },
  { id: 'u5', name: 'Taylor Swift', email: 'taylor@acme.com', avatar: 'TS' },
];

export function RestrictedAccessModal({
  isOpen,
  onClose,
  streamName,
  members,
  onAddMember,
  onRemoveMember,
  onUpdateRole,
}: RestrictedAccessModalProps) {
  const { isCommand, locale } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<AccessMember['role']>('viewer');
  const [showSearchResults, setShowSearchResults] = useState(false);

  const filteredUsers = AVAILABLE_USERS.filter(
    (user) =>
      !members.some((m) => m.id === user.id) &&
      (user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleAddUser = (user: (typeof AVAILABLE_USERS)[0]) => {
    onAddMember(user.email, selectedRole);
    setSearchQuery('');
    setShowSearchResults(false);
  };

  const getRoleLabel = (role: AccessMember['role']) => {
    if (isCommand) {
      switch (role) {
        case 'viewer':
          return 'Observer';
        case 'editor':
          return 'Operator';
        case 'admin':
          return 'Commander';
      }
    }
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const getRoleColor = (role: AccessMember['role']) => {
    switch (role) {
      case 'viewer':
        return 'text-text-secondary';
      case 'editor':
        return isCommand ? 'text-primary-400' : 'text-blue-500';
      case 'admin':
        return isCommand ? 'text-warning-400' : 'text-amber-500';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className={`
            w-full max-w-lg overflow-hidden rounded-xl shadow-2xl
            ${
              isCommand
                ? 'bg-surface-900 border border-surface-600 shadow-glow'
                : 'bg-surface-800 border border-surface-600'
            }
          `}
        >
          {/* Header */}
          <div
            className={`
              p-5 border-b border-surface-600
              ${isCommand ? 'bg-surface-800/50' : ''}
            `}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={`
                    w-10 h-10 rounded-lg flex items-center justify-center
                    ${isCommand ? 'bg-purple-500/20 border border-purple-500/30' : 'bg-orange-500/10'}
                  `}
                >
                  <Shield
                    className={`w-5 h-5 ${isCommand ? 'text-purple-400' : 'text-orange-500'}`}
                  />
                </div>
                <div>
                  <h2
                    className={`
                      font-semibold text-text-primary
                      ${isCommand ? 'text-sm font-mono uppercase tracking-wider' : 'text-lg'}
                    `}
                  >
                    {isCommand ? 'Access Control' : 'Manage Access'}
                  </h2>
                  <p
                    className={`
                      text-text-muted mt-0.5
                      ${isCommand ? 'text-[10px] font-mono' : 'text-sm'}
                    `}
                  >
                    {streamName}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-text-muted hover:text-text-primary hover:bg-surface-700 rounded-lg transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-5 space-y-5 max-h-[60vh] overflow-y-auto">
            {/* Add People */}
            <div>
              <label
                className={`
                  block mb-2 font-medium text-text-primary
                  ${isCommand ? 'text-xs font-mono uppercase tracking-wider' : 'text-sm'}
                `}
              >
                {isCommand ? 'Grant Clearance' : 'Add People'}
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowSearchResults(e.target.value.length > 0);
                    }}
                    onFocus={() => searchQuery.length > 0 && setShowSearchResults(true)}
                    placeholder={isCommand ? 'Search personnel...' : 'Search by name or email...'}
                    aria-label={isCommand ? 'Grant Clearance' : 'Add People'}
                    className={`
                      w-full pl-10 pr-3 py-2 rounded-lg
                      bg-surface-700 border border-surface-600
                      text-text-primary placeholder-text-muted
                      focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                      ${isCommand ? 'text-xs font-mono' : 'text-sm'}
                    `}
                  />

                  {/* Search Results Dropdown */}
                  <AnimatePresence>
                    {showSearchResults && searchQuery.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className={`
                          absolute top-full left-0 right-0 mt-1 z-10
                          rounded-lg shadow-xl overflow-hidden max-h-48 overflow-y-auto
                          ${
                            isCommand
                              ? 'bg-surface-800 border border-surface-600'
                              : 'bg-surface-700 border border-surface-600'
                          }
                        `}
                      >
                        {filteredUsers.length === 0 ? (
                          <div
                            className={`
                              px-3 py-4 text-center text-text-muted
                              ${isCommand ? 'text-xs font-mono' : 'text-sm'}
                            `}
                          >
                            {isCommand ? 'No personnel found' : 'No results found'}
                          </div>
                        ) : (
                          filteredUsers.map((user) => (
                          <button
                            key={user.id}
                            onClick={() => handleAddUser(user)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface-600 transition-colors text-left"
                          >
                            <div
                              className={`
                                w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium
                                ${isCommand ? 'bg-primary-500/20 text-primary-400' : 'bg-primary-500 text-white'}
                              `}
                            >
                              {user.avatar}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div
                                className={`
                                  text-text-primary truncate
                                  ${isCommand ? 'text-xs font-mono' : 'text-sm font-medium'}
                                `}
                              >
                                {user.name}
                              </div>
                              <div
                                className={`
                                  text-text-muted truncate
                                  ${isCommand ? 'text-[10px] font-mono' : 'text-xs'}
                                `}
                              >
                                {user.email}
                              </div>
                            </div>
                            <UserPlus className="w-4 h-4 text-text-muted" />
                          </button>
                          ))
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value as AccessMember['role'])}
                  aria-label="Select role"
                  className={`
                    px-3 py-2 rounded-lg
                    bg-surface-700 border border-surface-600
                    text-text-primary
                    focus:outline-none focus:ring-2 focus:ring-primary-500
                    ${isCommand ? 'text-xs font-mono uppercase' : 'text-sm'}
                  `}
                >
                  <option value="viewer">{getRoleLabel('viewer')}</option>
                  <option value="editor">{getRoleLabel('editor')}</option>
                  <option value="admin">{getRoleLabel('admin')}</option>
                </select>
              </div>
            </div>

            {/* Current Members */}
            <div>
              <h3
                className={`
                  mb-3 font-medium text-text-primary
                  ${isCommand ? 'text-xs font-mono uppercase tracking-wider' : 'text-sm'}
                `}
              >
                {isCommand ? 'Cleared Personnel' : 'People with Access'}{' '}
                <span className="text-text-muted">({members.length})</span>
              </h3>

              {members.length === 0 ? (
                <div
                  className={`
                    text-center py-8 text-text-muted
                    ${isCommand ? 'text-xs font-mono' : 'text-sm'}
                  `}
                >
                  {isCommand
                    ? 'No personnel have been granted clearance'
                    : 'No one has been added yet'}
                </div>
              ) : (
                <div className="space-y-2">
                  {members.map((member) => (
                    <div
                      key={member.id}
                      className={`
                        flex items-center justify-between p-3 rounded-lg
                        ${
                          isCommand
                            ? 'bg-surface-800 border border-surface-700'
                            : 'bg-surface-700/50 border border-surface-600'
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`
                            w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium
                            ${isCommand ? 'bg-surface-700 text-primary-400 border border-surface-600' : 'bg-primary-500 text-white'}
                          `}
                        >
                          {member.avatar || member.name.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div
                            className={`
                              text-text-primary
                              ${isCommand ? 'text-xs font-mono' : 'text-sm font-medium'}
                            `}
                          >
                            {member.name}
                          </div>
                          <div
                            className={`
                              text-text-muted
                              ${isCommand ? 'text-[10px] font-mono' : 'text-xs'}
                            `}
                          >
                            {member.email}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <select
                          value={member.role}
                          onChange={(e) =>
                            onUpdateRole(member.id, e.target.value as AccessMember['role'])
                          }
                          aria-label="Change role"
                          className={`
                            px-2 py-1 rounded
                            bg-surface-800 border border-surface-600
                            ${getRoleColor(member.role)}
                            focus:outline-none focus:ring-1 focus:ring-primary-500
                            ${isCommand ? 'text-[10px] font-mono uppercase' : 'text-xs'}
                          `}
                        >
                          <option value="viewer">{getRoleLabel('viewer')}</option>
                          <option value="editor">{getRoleLabel('editor')}</option>
                          <option value="admin">{getRoleLabel('admin')}</option>
                        </select>

                        <button
                          onClick={() => onRemoveMember(member.id)}
                          className="p-1.5 text-text-muted hover:text-danger-400 hover:bg-danger-500/10 rounded transition-colors"
                          title={isCommand ? 'Revoke clearance' : 'Remove access'}
                          aria-label={isCommand ? 'Revoke clearance' : 'Remove access'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div
            className={`
              p-4 border-t border-surface-600 flex justify-end gap-3
              ${isCommand ? 'bg-surface-800/50' : 'bg-surface-700/30'}
            `}
          >
            <Button variant="secondary" onClick={onClose}>
              {locale.cancel}
            </Button>
            <Button onClick={onClose}>
              <Check className="w-4 h-4 mr-2" />
              {isCommand ? 'Confirm' : 'Done'}
            </Button>
          </div>
        </motion.div>
      </motion.div>
      )}
    </AnimatePresence>
  );
}
